/**
 * The viewer shell.
 *
 * §28 sets the desktop arrangement, §27 the phone one, §12 the controls. The
 * rule behind all of it is §16 — the document is the important thing on screen,
 * so the chrome is monochrome, small and quiet, and only the tools that apply
 * to what is open are shown.
 *
 * Editing is gated exactly as §4.7–4.10 require: everything opens read-only,
 * Edit creates a protected working copy, and saving produces a new file unless
 * the user explicitly says otherwise.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FORMATS_BY_ID, LIMITS_BY_TIER, canEdit, formatFileSize, formatRelativeTime,
} from '@moduviewer/core';
import { buildFileInfo, openFile } from '@moduviewer/app';
import type { OpenedFile, OpenStatus } from '@moduviewer/app';
import type { OriginalRef, PlatformTier } from '@moduviewer/core';
import {
  addAnnotation, applyEdit, clearAnnotations, createDefaultRegistry, getCell,
  parseRef, removeAnnotation, removeEntries, renderImage, restoreEntries,
  setCell, setHwpxParagraphText, setParagraphText, setShapeText, withCell,
  setOdtParagraphText, setOdpShapeText, setTrim,
} from '@moduviewer/handlers';
import type {
  ArchiveModel, AudioModel, CsvModel, DocxModel, EpubModel, FormatHandler, HwpxModel,
  ImageModel, NativeImageModel, PdfModel, PptxModel, RgbColor,
  TextModel, VideoModel, XlsxModel, XlsxSheet,
} from '@moduviewer/handlers';
import { Icon, iconForFormat } from '@moduviewer/design/icon';
import type { IconName } from '@moduviewer/design/icon';
import { ConfirmDialog, Toasts } from './components/dialog.js';
import { RecoveryOffer } from './components/recovery.js';
import { RecentFiles } from './components/recent.js';
import { Favourites } from './components/favourites.js';
import { rememberRecentFile } from './lib/recent-files.js';
import { isFavourite, toggleFavourite } from './lib/favourites.js';
import { NoticesPanel } from './components/notices.js';
import { InstallButton } from './components/install-button.js';
import { BrowserFileStore } from './platform/browser-store.js';
import {
  desktopPathForFile, ElectronFileStore, isDesktop,
  onDesktopCommand, onIncomingFile, onOpenFailed,
} from './platform/electron-store.js';
import {
  CapacitorFileStore, isMobileShell, mobilePlatform,
  onMobileIncomingFile, onMobileOpenFailed, onMobilePersistNow,
} from './platform/capacitor-store.js';
import { useDocumentSession } from './lib/session.js';
import { createIsolatedRegistry } from './workers/parse-worker-client.js';
import { MESSAGES, t } from './lib/messages.js';
import { TextViewer } from './viewers/text-viewer.js';
import { TextEditor } from './viewers/text-editor.js';
import { ImageViewer } from './viewers/image-viewer.js';
import { ImageTools, RegionSelector } from './viewers/image-tools.js';
import type { RegionTool } from './viewers/image-tools.js';
import { SheetViewer, moveSelection } from './viewers/sheet-viewer.js';
import { FormulaBar } from './viewers/formula-bar.js';
import { PdfViewer } from './viewers/pdf-viewer.js';
import { AudioViewer } from './viewers/audio-viewer.js';
import { VideoViewer } from './viewers/video-viewer.js';
import { NativeImageViewer } from './viewers/native-image-viewer.js';
import { AnnotationToolbar } from './viewers/pdf-annotations.js';
import type { AnnotationTool } from './viewers/pdf-annotations.js';
import { DocxViewer } from './viewers/docx-viewer.js';
import { SlideViewer } from './viewers/slide-viewer.js';
import { HwpxViewer } from './viewers/hwpx-viewer.js';
import { ArchiveViewer } from './viewers/archive-viewer.js';
import { EpubViewer } from './viewers/epub-viewer.js';

/**
 * One App, three hosts.
 *
 * The desktop shell provides a real filesystem through IPC; the mobile shells
 * provide one through a native plugin, having copied the incoming file out of
 * a permission that was about to expire; the browser provides what a browser
 * can. All three satisfy the same port, so nothing below this line knows which
 * it is running on.
 */
const desktop = isDesktop();
const mobile = isMobileShell();
const store = desktop ? new ElectronFileStore()
  : mobile ? new CapacitorFileStore()
    : new BrowserFileStore();

/**
 * The tier decides the resource budgets (§33).
 *
 * A phone is killed for exceeding them; a desktop merely slows down. Reading
 * the platform rather than guessing from the viewport is what makes the two
 * different in the way that matters.
 */
const tier: PlatformTier = desktop ? 'desktop'
  : mobilePlatform() === null ? 'web'
    : lowMemoryDevice() ? 'mobile-low' : 'mobile';

/**
 * `navigator.deviceMemory` is Chromium-only and not in the DOM types, so it is
 * read defensively. A device that will not say is treated as ordinary rather
 * than as constrained: the low tier trades capability for safety, and applying
 * it to a phone that did not need it would be its own kind of wrong answer.
 */
function lowMemoryDevice(): boolean {
  const gb = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  return typeof gb === 'number' && gb <= 3;
}

/**
 * Whether a handle survives the session.
 *
 * On the desktop and on a phone a handle is a real path, so a recent-files
 * list can reopen one. In a browser it is a reference to a `File` the page was
 * handed, and it dies with the tab — offering a list there would produce rows
 * that fail on click, which §19 says not to build.
 */
const canReopenHandles = desktop || mobile;

const registry = createDefaultRegistry();

/**
 * The registry used for *opening*. It runs the heavy document, spreadsheet,
 * presentation and audio parsers in a Worker (§30, §33), falling back to the
 * main-thread `registry` on any Worker failure. Saving still uses `registry`
 * directly — an export operates on a model already in memory, so there is
 * nothing to isolate. PDF and images are not Worker-safe and stay inline.
 */
const openRegistry = createIsolatedRegistry(
  registry,
  () => new Worker(new URL('./workers/parse-worker.ts', import.meta.url), { type: 'module' }),
);

type Theme = 'auto' | 'light' | 'dark';

interface ViewState {
  zoom: number;
  fit: boolean;
  mode: 'source' | 'prose';
  wrap: boolean;
  search: string;
  selectedCell: string | null;
  sheetIndex: number;
  page: number;
  showRail: boolean;
  showInfo: boolean;
  showNotes: boolean;
  regionTool: RegionTool;
  annotationTool: AnnotationTool;
  annotationColor: RgbColor;
}

const INITIAL_VIEW: ViewState = {
  zoom: 1, fit: true, mode: 'prose', wrap: true, search: '',
  selectedCell: null, sheetIndex: 0, page: 1,
  showRail: false, showInfo: false, showNotes: true, regionTool: null,
  annotationTool: null, annotationColor: [1, 0.92, 0.23],
};

export function App(): React.JSX.Element {
  const [file, setFile] = useState<OpenedFile | null>(null);
  const [status, setStatus] = useState<OpenStatus | null>(null);
  const [failure, setFailure] = useState<{ key: string; detail: string } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [theme, setTheme] = useState<Theme>('auto');
  const [notices, setNotices] = useState(false);
  // Bumped when a favourite is pinned or unpinned, to redraw the list (§10).
  const [favouritesTick, setFavouritesTick] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (store instanceof BrowserFileStore) void store.init();
  }, []);
  useEffect(() => { document.documentElement.dataset['theme'] = theme; }, [theme]);

  /**
   * Open a file the shell handed us — double-click, "Open with", drag onto the
   * icon, or the menu. §9 requires all of those to work whether the app was
   * closed or already showing another document; by the time it reaches here
   * they are indistinguishable.
   */
  const openHandle = useCallback(async (
    handle: string, displayName: string, source: OriginalRef['source'],
  ) => {
    setFailure(null);
    setStatus('status.checkingFile');
    try {
      const opened = await openFile(
        { handle, displayName, source },
        { store, registry: openRegistry, tier, signal: new AbortController().signal, onStatus: setStatus },
      );
      setFile(opened);
      // §10, and §29 in the same breath: the list is only useful if the host
      // can reopen a handle, and only allowed if the file is not sensitive.
      // Both questions are answered here, where every open passes through.
      rememberRecentFile({
        handle,
        displayName,
        formatId: opened.detection.formatId,
        sizeBytes: opened.original.sizeBytes,
        sensitive: opened.assessment.sensitive,
        reopenable: canReopenHandles,
      }, Date.now());
    } catch (err) {
      const e = err as { messageKey?: string; message?: string };
      setFailure({ key: e.messageKey ?? 'error.unableToRead', detail: e.message ?? '' });
      setFile(null);
    } finally {
      setStatus(null);
    }
  }, []);

  /**
   * Open bytes the app produced itself — an entry taken out of an archive.
   *
   * It goes through the same pipeline as a file from the shell, detection and
   * security assessment included. Being inside an archive we already opened
   * earns an entry no trust at all (§21).
   */
  const openBytes = useCallback(async (bytes: Uint8Array, name: string) => {
    // All three stores can stage bytes; the difference is only where they land.
    const handle = await (store as {
      stageBytes(bytes: Uint8Array, name: string): Promise<string>;
    }).stageBytes(bytes, name);
    await openHandle(handle, name, 'file-picker');
  }, [openHandle]);

  /**
   * A file shared *into* the installed web app (§9, §36).
   *
   * On Android, sharing a file to the app lands as a POST the service worker
   * catches and stashes; the app arrives at `?shared=1` and picks it up here.
   * This is how an installed web app opens a file another app sent it — the
   * closest a PWA gets to being in the "open with" list.
   */
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has('shared')) return;
    void (async () => {
      try {
        const cache = await caches.open('modu-shared');
        const hit = await cache.match('shared-file');
        // Clean the flag off the URL either way, so a refresh does not re-open.
        window.history.replaceState(null, '', window.location.pathname);
        if (!hit) return;
        const name = decodeURIComponent(hit.headers.get('x-filename') ?? 'shared');
        const bytes = new Uint8Array(await hit.arrayBuffer());
        await cache.delete('shared-file');
        await openBytes(bytes, name);
      } catch { /* a share that cannot be read is not worth an error screen (§34) */ }
    })();
  }, [openBytes]);

  /**
   * A file opened *with* the installed web app (desktop, §9).
   *
   * On desktop Chromium the `file_handlers` manifest entry puts the app in the
   * OS "open with" menu; the chosen file arrives through the launch queue.
   */
  useEffect(() => {
    const queue = (window as unknown as {
      launchQueue?: { setConsumer(fn: (params: { files?: FileSystemFileHandle[] }) => void): void };
    }).launchQueue;
    if (!queue) return;
    queue.setConsumer((params) => {
      const handle = params.files?.[0];
      if (!handle) return;
      void (async () => {
        try {
          const file = await (handle as unknown as { getFile(): Promise<File> }).getFile();
          const bytes = new Uint8Array(await file.arrayBuffer());
          await openBytes(bytes, file.name);
        } catch { /* ignore an unreadable launch */ }
      })();
    });
  }, [openBytes]);

  useEffect(() => {
    if (!mobile) return;
    // The native side has been holding the launch intent since before this
    // code existed; saying so is what releases it.
    void (store as CapacitorFileStore).ready();
    const off = [
      onMobileIncomingFile((f) => void openHandle(f.handle, f.displayName, f.source)),
      onMobileOpenFailed((info) => setFailure({ key: info.messageKey, detail: info.path })),
      // §23: a backgrounded phone app may never run again. This is the last
      // moment at which an edit can still be written down.
      onMobilePersistNow(() => {
        window.dispatchEvent(new CustomEvent('modu:persist-now'));
      }),
    ];
    return () => { for (const dispose of off) dispose(); };
  }, [openHandle]);

  useEffect(() => {
    if (!desktop) return;
    const off = [
      onIncomingFile((f) => void openHandle(f.handle, f.displayName, f.source)),
      onOpenFailed((info) => setFailure({ key: info.messageKey, detail: info.path })),
      onDesktopCommand((command) => {
        if (command === 'find') document.querySelector<HTMLInputElement>('[data-search]')?.focus();
        if (command === 'info') window.dispatchEvent(new KeyboardEvent('keydown', { key: 'i' }));
        if (command === 'save' || command === 'save-as') {
          window.dispatchEvent(new KeyboardEvent('keydown', { key: 's', ctrlKey: true }));
        }
      }),
    ];
    // The incoming-file subscription is now live. The screenshot harness waits
    // for this flag before delivering a file: a file sent before this effect
    // runs is delivered to nobody and the frame photographs the empty state.
    document.documentElement.setAttribute('data-modu-open-ready', '1');
    return () => { for (const dispose of off) dispose(); };
  }, [openHandle]);

  const open = useCallback(async (incoming: File) => {
    // On the desktop a dropped file has a real path behind it, and using that
    // path makes the drop identical to a double-click: same store, same working
    // copy, same "save over the original" offer. Reading it as an anonymous
    // blob instead would silently downgrade the session to save-as-only.
    if (!(store instanceof BrowserFileStore)) {
      const path = desktopPathForFile(incoming);
      if (path) { await openHandle(path, incoming.name, 'drag-drop'); return; }
      setFailure({ key: 'error.unableToRead', detail: incoming.name });
      return;
    }
    setFailure(null);
    setStatus('status.checkingFile');
    const handle = store.register(incoming);
    try {
      const opened = await openFile(
        {
          handle,
          displayName: incoming.name,
          declaredMime: incoming.type || undefined,
          source: 'drag-drop',
        },
        { store, registry: openRegistry, tier, signal: new AbortController().signal, onStatus: setStatus },
      );
      setFile(opened);
    } catch (err) {
      const e = err as { messageKey?: string; message?: string };
      setFailure({ key: e.messageKey ?? 'error.unableToRead', detail: e.message ?? '' });
      setFile(null);
    } finally {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    const over = (e: DragEvent) => { e.preventDefault(); setDragging(true); };
    const leave = (e: DragEvent) => { if (e.relatedTarget === null) setDragging(false); };
    const drop = (e: DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const dropped = e.dataTransfer?.files?.[0];
      if (dropped) void open(dropped);
    };
    window.addEventListener('dragover', over);
    window.addEventListener('dragleave', leave);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragover', over);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('drop', drop);
    };
  }, [open]);

  return (
    <div className="shell" data-dragging={dragging ? 'true' : undefined}>
      <input
        ref={inputRef}
        type="file"
        className="visually-hidden"
        onChange={(e) => {
          const picked = e.target.files?.[0];
          if (picked) void open(picked);
          e.target.value = '';
        }}
      />

      {file === null ? (
        <>
          <IdleTopBar theme={theme} onTheme={setTheme} onOpen={() => inputRef.current?.click()} />
          <EmptyState
            status={status}
            failure={failure}
            onOpen={() => inputRef.current?.click()}
            recovery={
              <>
                <RecoveryOffer
                  store={store}
                  onRecover={(item, bytes) => openBytes(
                    bytes, item.displayName ?? '복구한 문서',
                  )}
                />
                <Favourites
                  key={`fav-${favouritesTick}`}
                  store={store}
                  reopenable={canReopenHandles}
                  onOpen={(fav) => void openHandle(
                    fav.handle, fav.displayName, 'recent',
                  )}
                />
                <RecentFiles
                  store={store}
                  reopenable={canReopenHandles}
                  onOpen={(recent) => void openHandle(
                    recent.handle, recent.displayName, 'recent',
                  )}
                  onFavouritesChanged={() => setFavouritesTick((n) => n + 1)}
                />
              </>
            }
          />
          <IdleStatusBar onNotices={() => setNotices(true)} />
        </>
      ) : (
        <Workspace
          key={file.session.id}
          file={file}
          theme={theme}
          onTheme={setTheme}
          onOpen={() => inputRef.current?.click()}
          onOpenBytes={openBytes}
          onClose={() => setFile(null)}
        />
      )}

      {notices ? <NoticesPanel onClose={() => setNotices(false)} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * workspace — one open document
 * ------------------------------------------------------------------ */

function Workspace(
  { file, theme, onTheme, onOpen, onOpenBytes, onClose }: {
    file: OpenedFile; theme: Theme; onTheme: (t: Theme) => void;
    onOpen: () => void;
    onOpenBytes: (bytes: Uint8Array, name: string) => Promise<void>;
    onClose: () => void;
  },
): React.JSX.Element {
  const handler = useMemo(
    () => registry.get(file.detection.formatId ?? '') as unknown as FormatHandler<unknown>,
    [file],
  );
  const api = useDocumentSession({ file, handler, store });
  const [view, setView] = useState<ViewState>({
    ...INITIAL_VIEW,
    mode: file.detection.formatId === 'markdown' ? 'prose' : 'source',
    // Page-based documents (PDF, slides) open with the thumbnail rail — but only
    // where it sits beside the page. On a phone the rail is a fixed overlay
    // drawer (§27), so defaulting it open would cover the document the reader
    // came to see. There, the page shows first and the rail is a deliberate tap.
    showRail: file.preview.kind === 'pages'
      && !window.matchMedia('(max-width: 860px)').matches,
  });

  const patch = useCallback((next: Partial<ViewState>) => {
    setView((current) => ({ ...current, ...next }));
  }, []);

  // §10: is this document pinned? Read once per opened file; the toggle keeps
  // it in step without re-reading storage.
  const [pinned, setPinned] = useState(() => isFavourite(file.original.handle));

  const spec = file.detection.formatId ? FORMATS_BY_ID.get(file.detection.formatId) ?? null : null;

  /* §28 keyboard shortcuts. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField = target !== null && /^(INPUT|TEXTAREA)$/.test(target.tagName);
      const mod = e.ctrlKey || e.metaKey;

      if (mod && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (api.editing) api.saveAs();
        return;
      }
      if (mod && e.key.toLowerCase() === 'o') { e.preventDefault(); onOpen(); return; }
      if (inField) { if (e.key === 'Escape') target.blur(); return; }

      if (mod && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        document.querySelector<HTMLInputElement>('[data-search]')?.focus();
        return;
      }
      if (mod && (e.key === '=' || e.key === '+')) {
        e.preventDefault(); patch({ zoom: Math.min(8, view.zoom * 1.25), fit: false }); return;
      }
      if (mod && e.key === '-') {
        e.preventDefault(); patch({ zoom: Math.max(0.1, view.zoom / 1.25), fit: false }); return;
      }
      if (mod && e.key === '0') { e.preventDefault(); patch({ zoom: 1, fit: false }); return; }
      if (!mod && e.key.toLowerCase() === 'i') { patch({ showInfo: !view.showInfo }); return; }
      if (e.key === 'Escape') { patch({ showInfo: false, search: '', regionTool: null }); return; }

      if (file.preview.kind === 'sheets' || file.preview.kind === 'table') {
        const next = moveSelection(view.selectedCell, e.key, 1_048_576, 16_384);
        if (next !== view.selectedCell) { e.preventDefault(); patch({ selectedCell: next }); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [api, file, view, patch, onOpen]);

  const editable = file.editable && handler?.exportBytes !== undefined;

  return (
    <>
      <header className="topbar">
        <button
          className="btn btn--icon tip"
          data-tip="탐색 패널"
          aria-label="탐색 패널"
          aria-pressed={view.showRail}
          onClick={() => patch({ showRail: !view.showRail })}
        >
          <Icon name="sidebar" />
        </button>
        <span className="sep" />

        <div className="file-id">
          <Icon
            name={iconForFormat(file.detection.formatId, spec?.family)}
            size={20}
            className="file-id__icon"
          />
          <span className="file-id__text">
            <span className="file-id__name truncate" data-document-name>{file.request.displayName}</span>
            <span className="file-id__meta">
              <span>{spec?.label ?? '알 수 없는 형식'}</span>
              <span aria-hidden="true">·</span>
              <span className="tnum">{formatFileSize('ko', file.original.sizeBytes)}</span>
              {api.editing ? (
                <>
                  <span aria-hidden="true">·</span>
                  <SaveState api={api} />
                </>
              ) : null}
            </span>
          </span>
        </div>

        <span className="spacer" />

        <label className="visually-hidden" htmlFor="search">문서 내 검색</label>
        <input
          id="search"
          data-search
          className="field"
          style={{ width: 168 }}
          placeholder="검색"
          value={view.search}
          onChange={(e) => patch({ search: e.target.value })}
        />
        <span className="sep" />

        {api.editing ? (
          <>
            <button className="btn" onClick={() => api.discard()}>편집 취소</button>
            <button className="btn btn--primary" onClick={() => api.saveAs()} disabled={api.saving}>
              <Icon name="save-as" size={15} />
              새 파일로 저장
            </button>
          </>
        ) : editable ? (
          <button className="btn btn--outline" onClick={() => api.beginEdit()}>
            <Icon name="edit" size={15} />
            편집
          </button>
        ) : (
          <span className="badge" title="이 형식은 편집을 지원하지 않습니다">읽기 전용</span>
        )}

        <span className="sep" />
        {canReopenHandles && !file.assessment.sensitive ? (
          <button
            className="btn btn--icon tip"
            data-tip={pinned ? '즐겨찾기에서 제거' : '즐겨찾기에 추가'}
            aria-label={pinned ? '즐겨찾기에서 제거' : '즐겨찾기에 추가'}
            aria-pressed={pinned}
            onClick={() => {
              setPinned(toggleFavourite({
                handle: file.original.handle,
                displayName: file.original.displayName,
                formatId: file.original.formatId,
                sizeBytes: file.original.sizeBytes,
                sensitive: file.assessment.sensitive,
                reopenable: canReopenHandles,
              }, Date.now()));
            }}
          >
            <Icon name={pinned ? 'star-filled' : 'star'} />
          </button>
        ) : null}
        <button
          className="btn btn--icon tip"
          data-tip="파일 정보 (I)"
          aria-label="파일 정보"
          aria-pressed={view.showInfo}
          onClick={() => patch({ showInfo: !view.showInfo })}
        >
          <Icon name="info" />
        </button>
        <button
          className="btn btn--icon tip"
          data-tip={theme === 'dark' ? '밝게' : '어둡게'}
          aria-label="테마 전환"
          onClick={() => onTheme(theme === 'dark' ? 'light' : 'dark')}
        >
          <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
        </button>
        <button className="btn btn--icon tip" data-tip="다른 파일 열기" aria-label="다른 파일 열기" onClick={onOpen}>
          <Icon name="open" />
        </button>
        <button className="btn btn--icon tip" data-tip="닫기" aria-label="닫기" onClick={onClose}>
          <Icon name="close" />
        </button>
      </header>

      {view.showRail ? <Rail file={file} view={view} onPatch={patch} /> : null}

      <main className="stage" data-viewer={file.preview.kind}>
        {api.editing ? (
          <div className="edit-banner">
            <Icon name="shield" size={15} className="edit-banner__icon" />
            <span>원본 파일은 변경되지 않습니다. 편집한 내용은 새 파일로 저장됩니다.</span>
          </div>
        ) : null}

        {api.editing && file.detection.formatId === 'pdf' ? (
          <AnnotationToolbar
            tool={view.annotationTool}
            color={view.annotationColor}
            count={(api.model as PdfModel).annotations.length}
            onTool={(tool) => patch({ annotationTool: tool })}
            onColor={(color) => patch({ annotationColor: color })}
            onClear={() => api.update(clearAnnotations(api.model as PdfModel))}
          />
        ) : api.editing && file.preview.kind === 'image' ? (
          <ImageTools
            model={api.model as ImageModel}
            tool={view.regionTool}
            onTool={(tool) => patch({ regionTool: tool })}
            onChange={(next) => api.update(next)}
          />
        ) : (
          <ViewToolbar file={file} view={view} onPatch={patch} />
        )}

        <Notices file={file} />
        <Stage file={file} api={api} view={view} onPatch={patch} onOpenBytes={onOpenBytes} />
      </main>

      {view.showInfo ? <InfoPanel file={file} api={api} /> : null}

      <footer className="statusbar">
        <span className="statusbar__item">
          <Icon name="shield" size={13} style={{ color: 'var(--ok)' }} />
          이 기기에서 처리됨
        </span>
        <span className="statusbar__item">
          <Icon name="check" size={13} />
          원본 변경 없음
        </span>
        <span className="spacer" />
        {file.preview.kind === 'pages' ? (
          <span className="statusbar__item tnum">
            {view.page} / {(file.document.model as PdfModel).pageCount}
          </span>
        ) : null}
        {view.selectedCell ? <span className="statusbar__item mono">{view.selectedCell}</span> : null}
        {!view.fit ? <span className="statusbar__item tnum">{Math.round(view.zoom * 100)}%</span> : null}
        {spec && !canEdit(spec.edit) ? <span className="statusbar__item">읽기 전용 형식</span> : null}
      </footer>

      {api.confirm ? <ConfirmDialog request={api.confirm} /> : null}
      <Toasts toasts={api.toasts} messages={MESSAGES} onDismiss={api.dismissToast} />
    </>
  );
}

/* ------------------------------------------------------------------ *
 * pieces
 * ------------------------------------------------------------------ */

function SaveState({ api }: { api: ReturnType<typeof useDocumentSession> }): React.JSX.Element {
  const state = api.saving ? 'saving' : api.dirty ? 'dirty' : api.lastSavedAt ? 'saved' : 'idle';
  const label = state === 'saving' ? '저장 중…'
    : state === 'dirty' ? '저장되지 않음'
      : state === 'saved' && api.lastSavedAt
        ? `${formatRelativeTime('ko', new Date(api.lastSavedAt), new Date())} 저장됨`
        : '편집 중';
  return <span className="save-state" data-state={state}>{label}</span>;
}

function ViewToolbar(
  { file, view, onPatch }: {
    file: OpenedFile; view: ViewState; onPatch: (p: Partial<ViewState>) => void;
  },
): React.JSX.Element | null {
  const kind = file.preview.kind;
  const zoomable = kind === 'pages' || kind === 'image';
  const textual = kind === 'text';
  if (!zoomable && !textual) return null;

  return (
    <div className="toolbar" role="toolbar" aria-label="보기 도구">
      {zoomable ? (
        <>
          <ToolButton icon="zoom-out" label="축소"
            onClick={() => onPatch({ zoom: Math.max(0.1, view.zoom / 1.25), fit: false })} />
          <span className="tnum" style={{
            minWidth: 46, textAlign: 'center',
            fontSize: 'var(--t-xs)', color: 'var(--text-secondary)',
          }}>
            {view.fit ? '맞춤' : `${Math.round(view.zoom * 100)}%`}
          </span>
          <ToolButton icon="zoom-in" label="확대"
            onClick={() => onPatch({ zoom: Math.min(8, view.zoom * 1.25), fit: false })} />
          <span className="sep" />
          <ToolButton icon="fit-page" label="화면 맞춤" active={view.fit}
            onClick={() => onPatch({ fit: true })} />
          <ToolButton icon="actual-size" label="실제 크기" active={!view.fit && view.zoom === 1}
            onClick={() => onPatch({ zoom: 1, fit: false })} />
        </>
      ) : null}

      {textual ? (
        <>
          {file.detection.formatId === 'markdown' ? (
            <>
              <ToolButton icon="file-text" label="문서" active={view.mode === 'prose'}
                onClick={() => onPatch({ mode: 'prose' })} />
              <ToolButton icon="file-code" label="원본" active={view.mode === 'source'}
                onClick={() => onPatch({ mode: 'source' })} />
              <span className="sep" />
            </>
          ) : null}
          <ToolButton icon="list" label="줄바꿈" active={view.wrap}
            onClick={() => onPatch({ wrap: !view.wrap })} />
        </>
      ) : null}
    </div>
  );
}

function ToolButton(
  { icon, label, onClick, active }: {
    icon: IconName; label: string; onClick: () => void; active?: boolean;
  },
): React.JSX.Element {
  return (
    <button className="btn btn--icon tip" data-tip={label} aria-label={label}
      aria-pressed={active ?? undefined} onClick={onClick}>
      <Icon name={icon} />
    </button>
  );
}

function Stage(
  { file, api, view, onPatch, onOpenBytes }: {
    file: OpenedFile;
    api: ReturnType<typeof useDocumentSession>;
    view: ViewState;
    onPatch: (p: Partial<ViewState>) => void;
    onOpenBytes: (bytes: Uint8Array, name: string) => Promise<void>;
  },
): React.JSX.Element {
  const kind = file.preview.kind;

  if (kind === 'archive') {
    const model = api.model as ArchiveModel;
    return (
      <ArchiveViewer
        model={model}
        bytes={file.source.bytes}
        ctx={{
          assessment: file.assessment,
          limits: LIMITS_BY_TIER[tier],
          signal: new AbortController().signal,
        }}
        editing={api.editing}
        search={view.search}
        onRemove={(name) => api.update(removeEntries(model, [name]))}
        onRestore={(name) => api.update(restoreEntries(model, [name]))}
        onPreview={(name, bytes) => void onOpenBytes(bytes, name.split('/').pop() ?? name)}
        onExtract={(name, bytes) => void api.saveExtracted(bytes, name.split('/').pop() ?? name)}
      />
    );
  }

  // A PDF, a Word document, a Hangul document and a book all present as pages,
  // but only the PDF is laid out already — the format decides which renderer,
  // not the preview kind.
  if (kind === 'pages' && file.detection.formatId === 'epub') {
    const model = api.model as EpubModel;
    return (
      <EpubViewer
        model={model}
        current={Math.min(view.page - 1, model.chapters.length - 1)}
        search={view.search}
        zoom={view.fit ? 1 : view.zoom}
        onSelect={(index) => onPatch({ page: index + 1 })}
      />
    );
  }

  if (kind === 'pages' && file.detection.formatId === 'hwpx') {
    return (
      <HwpxViewer
        model={api.model as HwpxModel}
        editing={api.editing}
        search={view.search}
        zoom={view.fit ? 1 : view.zoom}
        onEditParagraph={(index, text) => {
          try {
            api.update(setHwpxParagraphText(api.model as HwpxModel, index, text));
          } catch {
            // Paragraphs holding a picture, an equation or an object have no
            // edit affordance; a keyboard path that found one drops the edit.
          }
        }}
      />
    );
  }

  // DOCX, ODT, RTF and HWP share the document model and therefore the viewer.
  // DOCX and ODT differ only in the edit function; RTF and HWP are read-only,
  // so they render through the same viewer with editing off.
  if (kind === 'pages' && ['docx', 'odt', 'rtf', 'hwp'].includes(file.detection.formatId ?? '')) {
    const isOdt = file.detection.formatId === 'odt';
    return (
      <DocxViewer
        model={api.model as DocxModel}
        editing={api.editing}
        search={view.search}
        zoom={view.fit ? 1 : view.zoom}
        onEditParagraph={(index, text) => {
          try {
            api.update((isOdt ? setOdtParagraphText : setParagraphText)(
              api.model as DocxModel, index, text,
            ));
          } catch {
            // The edit function refuses paragraphs it cannot rebuild. The UI
            // already hides the affordance for those, so reaching here means
            // a keyboard path found one — dropping the edit is correct.
          }
        }}
      />
    );
  }

  if (kind === 'slides') {
    return (
      <SlideViewer
        model={api.model as PptxModel}
        current={Math.min(view.page - 1, (api.model as PptxModel).slides.length - 1)}
        editing={api.editing}
        search={view.search}
        showNotes={view.showNotes}
        onSelect={(index) => onPatch({ page: index + 1 })}
        onEditShape={(slideIndex, shapeId, text) => {
          try {
            const edit = file.detection.formatId === 'odp' ? setOdpShapeText : setShapeText;
            api.update(edit(api.model as PptxModel, slideIndex, shapeId, text));
          } catch {
            // Shapes the save path cannot rebuild have no edit affordance; a
            // keyboard path that reached one drops the edit rather than
            // writing something lossy.
          }
        }}
      />
    );
  }

  if (kind === 'pages') {
    const model = api.model as PdfModel;
    return (
      <PdfViewer
        bytes={file.source.bytes}
        zoom={view.fit ? 1.2 : view.zoom}
        onPageVisible={(p) => onPatch({ page: p + 1 })}
        annotating={api.editing ? {
          annotations: model.annotations,
          tool: view.annotationTool,
          color: view.annotationColor,
          onAdd: (annotation) => api.update(addAnnotation(model, annotation)),
          onRemove: (index) => api.update(removeAnnotation(model, index)),
        } : undefined}
      />
    );
  }

  if (kind === 'image') {
    // HEIC and other platform-decoded formats carry no pixels; they render
    // through an <img> the platform decodes, with an honest fallback (§20).
    const raw = api.model as { kind?: string };
    if (raw.kind === 'native-image') {
      return <NativeImageViewer model={api.model as NativeImageModel} bytes={file.source.bytes} />;
    }
    const model = api.model as ImageModel;
    const image = renderImage(model);
    const scale = view.fit ? 1 : view.zoom;
    return (
      <div className="canvas image-view" style={{ position: 'relative' }}>
        <div style={{ position: 'relative', lineHeight: 0 }}>
          <ImageViewer model={model} zoom={view.fit ? null : view.zoom} />
          {api.editing && view.regionTool ? (
            <RegionSelector
              tool={view.regionTool}
              width={image.width}
              height={image.height}
              displayWidth={image.width * scale}
              displayHeight={image.height * scale}
              onSelect={(rect) => {
                const tool = view.regionTool;
                if (tool === 'crop') api.update(applyEdit(model, { kind: 'crop', rect }));
                else if (tool === 'mosaic') {
                  const block = Math.max(6, Math.round(Math.min(rect.width, rect.height) / 8));
                  api.update(applyEdit(model, { kind: 'mosaic', rect, blockSize: block }));
                } else if (tool === 'blur') {
                  api.update(applyEdit(model, { kind: 'blur', rect, radius: 4 }));
                }
                onPatch({ regionTool: null });
              }}
            />
          ) : null}
        </div>
      </div>
    );
  }

  if (kind === 'sheets') {
    const model = api.model as XlsxModel;
    const sheet = model.sheets[view.sheetIndex] ?? model.sheets[0]!;
    return (
      <>
        <FormulaBar
          selected={view.selectedCell}
          cell={view.selectedCell ? getCell(sheet, view.selectedCell) : null}
          editable={api.editing}
          onCommit={(ref, value) =>
            api.update(setCell(model, view.sheetIndex, ref, { kind: 'text', text: value }))}
          onCommitAndMove={(ref, value) => {
            api.update(setCell(model, view.sheetIndex, ref, { kind: 'text', text: value }));
            onPatch({ selectedCell: moveSelection(ref, 'ArrowDown', 1_048_576, 16_384) });
          }}
        />
        <SheetViewer
          sheet={sheet}
          selected={view.selectedCell}
          onSelect={(ref) => onPatch({ selectedCell: ref })}
          editing={api.editing ? {
            onCommit: (ref, value) =>
              api.update(setCell(model, view.sheetIndex, ref, { kind: 'text', text: value })),
          } : undefined}
        />
        {model.sheets.length > 1 ? (
          <div className="sheet-tabs" role="tablist" aria-label="시트">
            {model.sheets.map((s, i) => (
              <button key={s.part} role="tab" className="sheet-tab"
                aria-selected={i === view.sheetIndex}
                onClick={() => onPatch({ sheetIndex: i, selectedCell: null })}>
                {s.name}
              </button>
            ))}
          </div>
        ) : null}
      </>
    );
  }

  if (kind === 'table') {
    const csvModel = api.model as CsvModel;
    const csvSheet = csvAsSheet(csvModel);
    /** A grid reference is a row and a column; a CSV model stores rows. */
    const commitCsv = (ref: string, value: string): void => {
      const parsed = parseRef(ref);
      if (!parsed) return;
      api.update(withCell(csvModel, parsed.row, parsed.col, value));
    };
    return (
      <>
        <FormulaBar
          selected={view.selectedCell}
          cell={view.selectedCell ? getCell(csvSheet, view.selectedCell) : null}
          editable={api.editing}
          onCommit={commitCsv}
          onCommitAndMove={(ref, value) => {
            commitCsv(ref, value);
            onPatch({ selectedCell: moveSelection(ref, 'ArrowDown', 1_048_576, 16_384) });
          }}
        />
        <SheetViewer
          sheet={csvSheet}
          selected={view.selectedCell}
          onSelect={(ref) => onPatch({ selectedCell: ref })}
          editing={api.editing ? { onCommit: commitCsv } : undefined}
        />
      </>
    );
  }

  if (kind === 'media') {
    const media = api.model as AudioModel | VideoModel;
    if (media.kind === 'video') {
      return <VideoViewer model={media} bytes={file.source.bytes} />;
    }
    const model = media;
    return (
      <AudioViewer
        model={model}
        bytes={file.source.bytes}
        editing={api.editing}
        onTrim={(startSec, endSec) => {
          try {
            api.update(setTrim(model, startSec, endSec));
          } catch {
            // A degenerate range is dropped rather than written; the button is
            // disabled for the whole-file case, so reaching here is rare.
          }
        }}
      />
    );
  }

  if (api.editing) {
    return (
      <TextEditor
        model={api.model as TextModel}
        wrap={view.wrap}
        onChange={(next) => api.update(next)}
      />
    );
  }

  return (
    <TextViewer
      model={api.model as TextModel}
      formatId={file.detection.formatId ?? 'txt'}
      mode={view.mode}
      wrap={view.wrap}
      search={view.search}
    />
  );
}

function csvAsSheet(model: CsvModel): XlsxSheet {
  const cells = [];
  let colCount = 0;
  for (let r = 0; r < model.rows.length; r++) {
    const row = model.rows[r]!;
    colCount = Math.max(colCount, row.length);
    for (let c = 0; c < row.length; c++) {
      const text = row[c] ?? '';
      const cleaned = text.replace(/,/g, '');
      const numeric = text !== '' && cleaned !== '' && !Number.isNaN(Number(cleaned))
        ? Number(cleaned) : null;
      cells.push({ ref: `${columnName(c)}${r + 1}`, row: r, col: c, text, numeric, formula: null });
    }
  }
  return { name: 'CSV', part: 'csv', cells, rowCount: model.rows.length, colCount };
}

function columnName(index: number): string {
  let n = index + 1;
  let name = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    name = String.fromCharCode(65 + rem) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
}

function Rail(
  { file, view, onPatch }: {
    file: OpenedFile; view: ViewState; onPatch: (p: Partial<ViewState>) => void;
  },
): React.JSX.Element {
  const pages = file.preview.kind === 'pages' ? (file.document.model as PdfModel).pageCount : 0;
  return (
    <nav className="rail" aria-label="페이지 탐색">
      <div className="rail__head">
        <span className="eyebrow">페이지</span>
        <span className="eyebrow tnum">{pages}</span>
      </div>
      <div className="rail__body">
        <div className="thumbs">
          {Array.from({ length: pages }, (_, i) => (
            <button key={i} className="thumb" aria-current={view.page === i + 1}
              onClick={() => {
                onPatch({ page: i + 1 });
                document.querySelectorAll('.pdf__page')[i]
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}>
              <span className="thumb__frame" />
              <span className="thumb__label">{i + 1}</span>
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
}

function Notices({ file }: { file: OpenedFile }): React.JSX.Element | null {
  const notices = file.document.warnings.filter((w) => w.severity !== 'info').slice(0, 2);
  if (notices.length === 0) return null;
  return (
    <>
      {notices.map((w, i) => (
        <div key={i} className={`notice notice--${w.severity === 'degraded' ? 'danger' : 'warn'}`}>
          <Icon name={w.severity === 'degraded' ? 'alert' : 'shield'} size={16} className="notice__icon" />
          <div className="notice__body">
            <div className="notice__title">{t(w.messageKey)}</div>
            {w.detail ? <div className="notice__detail">{w.detail}</div> : null}
          </div>
        </div>
      ))}
    </>
  );
}

function InfoPanel(
  { file, api }: { file: OpenedFile; api: ReturnType<typeof useDocumentSession> },
): React.JSX.Element {
  const info = useMemo(() => buildFileInfo({
    detection: file.detection,
    assessment: file.assessment,
    spec: file.spec,
    document: file.document,
    session: api.session,
    tier: 'web',
    validation: api.validation,
    workingCopyLocation: api.session.workingCopy ? '앱 전용 저장소' : null,
  }), [file, api.session, api.validation]);

  return (
    <aside className="panel" aria-label="파일 정보">
      <div className="panel__section">
        <h2 className="panel__title eyebrow">파일 정보</h2>
        <dl className="info-list">
          {info.rows.filter((r) => r.value || r.valueKey).map((row) => (
            <div key={row.labelKey} style={{ display: 'contents' }}>
              <dt>{t(row.labelKey)}</dt>
              <dd className="truncate" title={row.detail ?? undefined}>
                {row.value || t(row.valueKey ?? '')}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      {info.blocked.length > 0 ? (
        <div className="panel__section">
          <h2 className="panel__title eyebrow">차단된 기능</h2>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 'var(--s4)' }}>
            {info.blocked.map((b) => (
              <li key={b.kind} style={{ display: 'flex', gap: 'var(--s4)', alignItems: 'flex-start' }}>
                <Icon name="shield" size={15} style={{ color: 'var(--ok)', flex: 'none', marginTop: 2 }} />
                <span style={{ fontSize: 'var(--t-xs)', color: 'var(--text-secondary)' }}>{b.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {info.lossOnSave.length > 0 ? (
        <div className="panel__section">
          <h2 className="panel__title eyebrow">저장 시 손실 가능</h2>
          <ul style={{
            margin: 0, paddingLeft: 'var(--s6)', fontSize: 'var(--t-xs)',
            color: 'var(--text-secondary)', display: 'grid', gap: 'var(--s3)',
          }}>
            {info.lossOnSave.map((l, i) => <li key={i}>{l}</li>)}
          </ul>
        </div>
      ) : null}
    </aside>
  );
}

/* ------------------------------------------------------------------ *
 * idle state
 * ------------------------------------------------------------------ */

function IdleTopBar(
  { theme, onTheme, onOpen }: { theme: Theme; onTheme: (t: Theme) => void; onOpen: () => void },
): React.JSX.Element {
  return (
    <header className="topbar">
      <div className="file-id">
        <span className="file-id__text">
          <span className="file-id__name">모두뷰어</span>
        </span>
      </div>
      <span className="spacer" />
      <button className="btn btn--icon tip" data-tip={theme === 'dark' ? '밝게' : '어둡게'}
        aria-label="테마 전환" onClick={() => onTheme(theme === 'dark' ? 'light' : 'dark')}>
        <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
      </button>
      <button className="btn btn--outline" onClick={onOpen}>
        <Icon name="open" size={15} />
        열기
      </button>
    </header>
  );
}

function IdleStatusBar(
  { onNotices }: { onNotices: () => void },
): React.JSX.Element {
  return (
    <footer className="statusbar">
      <span className="statusbar__item">광고 없음 · 계정 없이 사용</span>
      <span className="spacer" />
      {/* §40 requires the notice to be reachable from the product, not only
          from the repository. The status bar is where it belongs: present,
          and not competing with the document. */}
      <button className="statusbar__link" onClick={onNotices}>오픈소스 고지</button>
      <span className="statusbar__item">최대 {formatFileSize('ko', LIMITS_BY_TIER[tier].maxFileBytes)}</span>
    </footer>
  );
}

function EmptyState(
  { status, failure, onOpen, recovery }: {
    status: OpenStatus | null;
    failure: { key: string; detail: string } | null;
    onOpen: () => void;
    /** The §23 offer, rendered above the hint when there is work to restore. */
    recovery?: React.ReactNode;
  },
): React.JSX.Element {
  return (
    <div className="empty">
      <div className="empty__inner">
        <span className="empty__mark"><Icon name="file" size={26} /></span>
        <h1>받은 파일을 여기에 놓으세요</h1>
        <p>
          PDF, 엑셀, 이미지, 텍스트를 광고 없이 바로 봅니다.
          파일은 이 기기 안에서만 처리되고 어디로도 전송되지 않습니다.
        </p>

        {failure ? (
          <div className="notice notice--danger"
            style={{ borderRadius: 'var(--r-md)', border: '1px solid var(--line)' }}>
            <Icon name="alert" size={16} className="notice__icon" />
            <div className="notice__body">
              <div className="notice__title" data-failure={failure.key}>{t(failure.key)}</div>
              {failure.detail ? <div className="notice__detail">{failure.detail}</div> : null}
            </div>
          </div>
        ) : null}

        {status ? (
          <div className="loading" style={{ minHeight: 'auto' }}>
            <div className="loading__bar" />
            <span data-status={status}>{t(status)}</span>
          </div>
        ) : (
          <div className="empty__actions">
            <button className="btn btn--primary" onClick={onOpen}>파일 선택</button>
            <InstallButton />
          </div>
        )}

        {recovery}

        <p className="empty__hint">
          <kbd>Ctrl</kbd> <kbd>O</kbd> 열기 · <kbd>Ctrl</kbd> <kbd>S</kbd> 저장 · <kbd>I</kbd> 파일 정보
        </p>
      </div>
    </div>
  );
}
